import { useEffect, useState, useCallback } from 'react'
import { GitCompare, Plus, X, Trash2, RefreshCw, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Finding {
  id: number
  resource: string
  expected_state: string
  actual_state: string
  severity: string
  source: string
  status: string
  detected_at: string
  resolved_at: string | null
  notes: string
}

const SEVS = ['critical', 'high', 'medium', 'low'] as const
const SEV_TONE: Record<string, string> = {
  critical: 'bg-rose/15 text-rose border-rose/30',
  high:     'bg-amber/15 text-amber-light border-amber/30',
  medium:   'bg-blue/15 text-blue-light border-blue/30',
  low:      'bg-emerald/15 text-emerald border-emerald/30',
}

export default function DriftPage() {
  const [items, setItems] = useState<Finding[]>([])
  const [bySev, setBySev] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('open')
  const [form, setForm] = useState({
    resource: '', expected_state: '', actual_state: '',
    severity: 'medium', source: 'manual', notes: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/sre/drift', { params: { status: statusFilter } })
      setItems(r.data?.findings || [])
      setBySev(r.data?.by_severity_open || {})
    } finally { setLoading(false) }
  }, [statusFilter])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.resource) return
    await api.post('/api/sre/drift', form)
    setShowForm(false)
    setForm({ ...form, resource: '', expected_state: '', actual_state: '', notes: '' })
    fetchAll()
  }

  const resolve = async (id: number) => {
    await api.post(`/api/sre/drift/${id}/resolve`, {})
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete finding?')) return
    await api.delete(`/api/sre/drift/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitCompare size={24} className="text-amber-light" /> Config Drift
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Declared IaC vs runtime. Push from Driftctl / terraform plan via <code className="font-mono">/api/sre/drift</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Log finding</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SEVS.map(s => (
          <div key={s} className="card p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">{s} (open)</div>
            <div className={clsx('text-2xl font-bold mt-1 font-mono',
              s === 'critical' ? 'text-rose' : s === 'high' ? 'text-amber-light' : s === 'medium' ? 'text-blue-light' : 'text-emerald'
            )}>{bySev[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {(['open', 'accepted', 'resolved', 'suppressed'] as const).map(s => (
          <button key={s} type="button" onClick={() => setStatusFilter(s)}
            className={clsx('badge text-[10px] px-2 py-1',
              statusFilter === s ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {s}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Log drift finding</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.resource} onChange={e => setForm({...form, resource: e.target.value})} placeholder='Resource e.g. aws_s3_bucket.logs' className="input py-1.5 md:col-span-2 font-mono" />
            <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} className="input py-1.5">
              {SEVS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="Source: driftctl / terraform / manual" className="input py-1.5" />
            <textarea value={form.expected_state} onChange={e => setForm({...form, expected_state: e.target.value})} rows={2} placeholder="Expected state" className="input py-1.5 font-mono md:col-span-2" />
            <textarea value={form.actual_state} onChange={e => setForm({...form, actual_state: e.target.value})} rows={2} placeholder="Actual state" className="input py-1.5 font-mono md:col-span-2" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={1} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        {items.length === 0 ? (
          <p className="text-center py-6 text-text-muted text-[11px] italic">No drift findings.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Resource</th>
                <th className="text-left py-2 px-2">Severity</th>
                <th className="text-left py-2 px-2">Source</th>
                <th className="text-left py-2 px-2">Detected</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(f => (
                <tr key={f.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2 font-mono text-blue-light">{f.resource}</td>
                  <td className="py-1.5 px-2">
                    <span className={clsx('badge text-[9px] uppercase border', SEV_TONE[f.severity])}>{f.severity}</span>
                  </td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{f.source}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{f.detected_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-1.5 px-2 text-right space-x-2">
                    {f.status === 'open' && (
                      <button type="button" onClick={() => resolve(f.id)} className="text-emerald" title="Resolve"><CheckCircle2 size={11} /></button>
                    )}
                    <button type="button" onClick={() => remove(f.id)} className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
